import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { bookingService } from '../../../services/bookingService';

const BookingManagementPanel = () => {
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    startDate: '',
    endDate: '',
    pickupLocation: '',
    dropoffLocation: '',
    withDriver: false,
    status: 'pending',
    notes: ''
  });

  useEffect(() => {
    loadBookings();

    // Subscribe to real-time changes
    const unsubscribe = bookingService?.subscribeToChanges((payload) => {
      if (payload?.eventType === 'INSERT') {
        setBookings(prev => [convertToDisplayFormat(payload?.new), ...prev]);
      } else if (payload?.eventType === 'UPDATE') {
        setBookings(prev => prev?.map(b => 
          b?.id === payload?.new?.id ? convertToDisplayFormat(payload?.new) : b
        ));
      } else if (payload?.eventType === 'DELETE') {
        setBookings(prev => prev?.filter(b => b?.id !== payload?.old?.id));
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [bookings, selectedStatus]);

  const convertToDisplayFormat = (booking) => ({
    id: booking?.id,
    vehicleId: booking?.vehicleId,
    driverId: booking?.driverId,
    customerName: booking?.customerName,
    customerEmail: booking?.customerEmail,
    customerPhone: booking?.customerPhone,
    startDate: booking?.startDate ? new Date(booking?.startDate)?.toLocaleDateString('fr-FR') : 'N/A',
    endDate: booking?.endDate ? new Date(booking?.endDate)?.toLocaleDateString('fr-FR') : 'N/A',
    pickupLocation: booking?.pickupLocation,
    dropoffLocation: booking?.dropoffLocation,
    withDriver: booking?.withDriver,
    totalDays: booking?.totalDays,
    vehicleCost: booking?.vehicleCost,
    driverCost: booking?.driverCost,
    totalCost: booking?.totalCost,
    status: booking?.status,
    notes: booking?.notes,
    createdAt: booking?.createdAt ? new Date(booking?.createdAt)?.toLocaleString('fr-FR') : 'N/A',
    vehicle: booking?.vehicle,
    driver: booking?.driver
  });

  const loadBookings = async () => {
    try {
      setLoading(true);
      const data = await bookingService?.getAll();
      setBookings(data?.map(convertToDisplayFormat));
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    if (selectedStatus === 'all') {
      setFilteredBookings(bookings);
    } else {
      setFilteredBookings(bookings?.filter(b => b?.status === selectedStatus));
    }
  };

  const handleStatusChange = async (bookingId, newStatus) => {
    try {
      await bookingService?.update(bookingId, { status: newStatus });
    } catch (err) {
      alert('Erreur lors de la mise à jour: ' + err?.message);
    }
  };

  const handleDelete = async (bookingId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette réservation?')) return;

    try {
      await bookingService?.delete(bookingId);
    } catch (err) {
      alert('Erreur lors de la suppression: ' + err?.message);
    }
  };

  const handleOpenModal = (booking = null) => {
    if (booking) {
      setEditingBooking(booking);
      setFormData({
        customerName: booking?.customerName || '',
        customerEmail: booking?.customerEmail || '',
        customerPhone: booking?.customerPhone || '',
        startDate: booking?.startDate || '',
        endDate: booking?.endDate || '',
        pickupLocation: booking?.pickupLocation || '',
        dropoffLocation: booking?.dropoffLocation || '',
        withDriver: booking?.withDriver || false,
        status: booking?.status || 'pending',
        notes: booking?.notes || ''
      });
    } else {
      setEditingBooking(null);
      setFormData({
        customerName: '',
        customerEmail: '',
        customerPhone: '',
        startDate: '',
        endDate: '',
        pickupLocation: '',
        dropoffLocation: '',
        withDriver: false,
        status: 'pending',
        notes: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingBooking(null);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    try {
      if (editingBooking) {
        await bookingService?.update(editingBooking?.id, formData);
      } else {
        await bookingService?.create(formData);
      }
      handleCloseModal();
      loadBookings();
    } catch (err) {
      setError(err?.message);
    }
  };

  const statusOptions = [
    { value: 'all', label: 'Toutes les réservations' },
    { value: 'pending', label: 'En attente' },
    { value: 'confirmed', label: 'Confirmées' },
    { value: 'active', label: 'Actives' },
    { value: 'completed', label: 'Terminées' },
    { value: 'cancelled', label: 'Annulées' }
  ];

  const getStatusColor = (status) => {
    const colors = {
      pending: 'var(--color-warning)',
      confirmed: 'var(--color-trust)',
      active: 'var(--color-success)',
      completed: 'var(--color-muted-foreground)',
      cancelled: 'var(--color-error)'
    };
    return colors?.[status] || 'var(--color-muted-foreground)';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'En attente',
      confirmed: 'Confirmée',
      active: 'Active',
      completed: 'Terminée',
      cancelled: 'Annulée'
    };
    return labels?.[status] || status;
  };

  const getStatusActions = (currentStatus) => {
    const actions = {
      pending: ['confirmed', 'cancelled'],
      confirmed: ['active', 'cancelled'],
      active: ['completed', 'cancelled'],
      completed: [],
      cancelled: []
    };
    return actions?.[currentStatus] || [];
  };

  if (loading) {
    return (
      <div className="bg-card rounded-xl shadow-premium border border-border p-8 text-center">
        <p className="text-muted-foreground">Chargement des réservations...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card rounded-xl shadow-premium border border-border p-8 text-center">
        <p className="text-error">Erreur: {error}</p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-card rounded-xl shadow-premium border border-border">
        <div className="p-4 md:p-6 border-b border-border">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <h3 className="text-lg md:text-xl font-bold text-foreground mb-1">
                Gestion des Réservations
              </h3>
              <p className="text-sm text-muted-foreground">
                Suivez et gérez toutes les réservations de véhicules
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Select
                options={statusOptions}
                value={selectedStatus}
                onChange={setSelectedStatus}
                className="w-48"
              />
              <Button 
                variant="default" 
                iconName="Plus" 
                iconPosition="left" 
                size="sm"
                onClick={() => handleOpenModal()}
              >
                Nouvelle Réservation
              </Button>
            </div>
          </div>
        </div>

        {error && (
          <div className="p-4 bg-error/10 border-b border-error/20">
            <p className="text-error text-sm">{error}</p>
          </div>
        )}

        <div className="p-4 md:p-6">
          <div className="space-y-4">
            {filteredBookings?.map((booking) => (
              <div
                key={booking?.id}
                className="bg-muted/50 rounded-xl p-4 hover:bg-muted transition-colors"
              >
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div
                      className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${getStatusColor(booking?.status)}15` }}
                    >
                      <Icon
                        name="Car"
                        size={24}
                        style={{ color: getStatusColor(booking?.status) }}
                      />
                    </div>

                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm md:text-base font-semibold text-foreground mb-1">
                        {booking?.customerName}
                      </h4>
                      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground mb-2">
                        <span className="flex items-center gap-1">
                          <Icon name="Mail" size={12} />
                          {booking?.customerEmail}
                        </span>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                          <Icon name="Phone" size={12} />
                          {booking?.customerPhone}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className="px-2 py-1 rounded text-xs font-medium"
                          style={{
                            backgroundColor: `${getStatusColor(booking?.status)}15`,
                            color: getStatusColor(booking?.status),
                          }}
                        >
                          {getStatusLabel(booking?.status)}
                        </span>
                        {booking?.withDriver && (
                          <span className="px-2 py-1 bg-primary/10 text-primary rounded text-xs font-medium">
                            Avec chauffeur
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col md:flex-row items-start md:items-center gap-3 lg:gap-4">
                    <div className="text-left md:text-right">
                      <p className="text-lg font-bold text-foreground">
                        ${booking?.totalCost?.toFixed(2) || '0.00'}
                      </p>
                      <p className="text-xs text-muted-foreground">{booking?.totalDays} jours</p>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleOpenModal(booking)}
                        className="w-9 h-9 rounded-lg bg-background hover:bg-primary/10 flex items-center justify-center transition-colors"
                      >
                        <Icon name="Edit" size={16} className="text-primary" />
                      </button>
                      {getStatusActions(booking?.status)?.map((action) => (
                        <button
                          key={action}
                          onClick={() => handleStatusChange(booking?.id, action)}
                          className="w-9 h-9 rounded-lg bg-background hover:bg-primary/10 flex items-center justify-center transition-colors"
                          title={getStatusLabel(action)}
                        >
                          <Icon
                            name={action === 'confirmed' ? 'Check' : action === 'active' ? 'Play' : action === 'completed' ? 'CheckCircle' : 'X'}
                            size={16}
                            style={{ color: getStatusColor(action) }}
                          />
                        </button>
                      ))}
                      <button
                        onClick={() => handleDelete(booking?.id)}
                        className="w-9 h-9 rounded-lg bg-background hover:bg-error/10 flex items-center justify-center transition-colors"
                      >
                        <Icon name="Trash2" size={16} className="text-error" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {filteredBookings?.length === 0 && (
              <div className="text-center py-12">
                <Icon name="Car" size={48} className="text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Aucune réservation trouvée</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl shadow-premium max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-foreground">
                  {editingBooking ? 'Modifier la Réservation' : 'Nouvelle Réservation'}
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Nom du client *"
                  value={formData?.customerName}
                  onChange={(e) => setFormData({ ...formData, customerName: e?.target?.value })}
                  required
                />
                <Input
                  label="Email *"
                  type="email"
                  value={formData?.customerEmail}
                  onChange={(e) => setFormData({ ...formData, customerEmail: e?.target?.value })}
                  required
                />
                <Input
                  label="Téléphone *"
                  value={formData?.customerPhone}
                  onChange={(e) => setFormData({ ...formData, customerPhone: e?.target?.value })}
                  required
                />
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Statut *</label>
                  <Select
                    options={statusOptions?.filter(opt => opt?.value !== 'all')}
                    value={formData?.status}
                    onChange={(value) => setFormData({ ...formData, status: value })}
                  />
                </div>
                <Input
                  label="Date de début *"
                  type="date"
                  value={formData?.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e?.target?.value })}
                  required
                />
                <Input
                  label="Date de fin *"
                  type="date"
                  value={formData?.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e?.target?.value })}
                  required
                />
                <Input
                  label="Lieu de prise en charge *"
                  value={formData?.pickupLocation}
                  onChange={(e) => setFormData({ ...formData, pickupLocation: e?.target?.value })}
                  required
                />
                <Input
                  label="Lieu de dépôt *"
                  value={formData?.dropoffLocation}
                  onChange={(e) => setFormData({ ...formData, dropoffLocation: e?.target?.value })}
                  required
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="withDriver"
                  checked={formData?.withDriver}
                  onChange={(e) => setFormData({ ...formData, withDriver: e?.target?.checked })}
                  className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                />
                <label htmlFor="withDriver" className="text-sm font-medium text-foreground">
                  Avec chauffeur
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Notes</label>
                <textarea
                  value={formData?.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e?.target?.value })}
                  rows={4}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div className="flex items-center gap-3 pt-4">
                <Button type="submit" variant="default" className="flex-1">
                  {editingBooking ? 'Mettre à jour' : 'Créer'}
                </Button>
                <Button type="button" variant="outline" onClick={handleCloseModal} className="flex-1">
                  Annuler
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default BookingManagementPanel;